import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PartnerFeedbackModal } from './PartnerFeedbackModal';
import { PartnerAutocomplete } from '@/components/ui/PartnerAutocomplete';
import { useTrainingPartnersFromData } from '@/hooks/useTrainingPartners';
import { usePartnerFeedback } from '@/hooks/usePartnerFeedback';
import { useQueryClient } from '@tanstack/react-query';
import { Training, TrainingPartner } from '@/types/trainings';
import { TrainingPartnerFeedback } from '@/types/feedback';
import { format } from 'date-fns';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { RatingSelector } from '@/components/ui/RatingSelector';

// Categorias de técnicas baseadas no padrão do projeto
const techniqueCategories = [
  {
    name: 'guard_types',
    techniques: [
      'Closed Guard',
      'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)',
      'Half Guard (e.g., Deep Half, Z-Guard, Knee Shield)',
      'Butterfly Guard',
      'Worm Guard',
      'Reverse De La Riva Guard (RDLR)',
      '50/50 Guard',
      'Sitting Guard',
      'No-Gi Specific Guards (e.g., Leg Pummeling, Ashi Garami)',
      'Other Guard'
    ],
  },
  {
    name: 'guard_passing',
    techniques: [
      'Pressure Passing (e.g., Knee Slice, Over Under, Stack Pass)',
      'Torque/Leg Drag Passing',
      'Leg Weave Passing',
      'Floating/Smash Passing',
      'Movement-Based Passing (e.g., Toreando, Matador)',
      'Folding Passes (e.g., Double Under)',
      'No-Gi Specific Passes (e.g., Head & Arm Pass, Darce Pass from Guard)',
      'Other Guard Pass'
    ],
  },
  {
    name: 'submissions',
    techniques: [
      'Armlocks (e.g., Armbar, Kimura, Americana, Omoplata)',
      'Chokes (e.g., Rear Naked Choke, Guillotine, Triangle, Cross Collar Choke, Darce, Anaconda, Bow and Arrow)',
      'Leg Locks (e.g., Heel Hook, Kneebar, Ankle Lock, Calf Slicer)',
      'Shoulder Locks (e.g., Wrist Lock, Omoplata, Americana)',
      'Spine/Neck Cranks (e.g., Twister)',
      'No-Gi Specific Submissions (e.g., Head & Arm Choke, Ezekiel from Mount)',
      'Other Submission'
    ],
  },
  {
    name: 'sweeps_takedowns',
    techniques: [
      'Guard Sweeps (e.g., Scissor Sweep, Hip Bump Sweep, Flower Sweep, Hook Sweeps)',
      'Half Guard Sweeps (e.g., Old School Sweep, Waiter Sweep)',
      'Butterfly Sweeps',
      'De La Riva Sweeps',
      'Takedowns from Standing (e.g., Double Leg, Single Leg, Judo Throws, Body Lock)',
      'Counter Takedowns',
      'Pulling Guard',
      'Other Sweep / Takedown'
    ],
  },
  {
    name: 'escapes_reversals',
    techniques: [
      'Mount Escapes (e.g., Upa, Elbow-Knee Escape)',
      'Back Escapes (e.g., Bridge & Roll, Hip Escape)',
      'Side Control Escapes (e.g., Hip Escape to Guard, Underhook Escapes)',
      'North South Escapes',
      'Submission Escapes (e.g., Armbar Escape, Triangle Escape)',
      'Turtle Escapes',
      'Half Guard Escapes',
      'Other Escape / Reversal'
    ],
  },
];

interface TrainingNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  training: Training | null;
  notesData?: any;
  onSave?: (trainingId: string, notes: string) => void; // Keep for backward compatibility
  onDataSaved?: () => void; // New callback for when data is saved
}

export const TrainingNotesModal = ({ 
  isOpen, 
  onClose, 
  training, 
  notesData: propNotesData,
  onSave,
  onDataSaved
}: TrainingNotesModalProps) => {
  const { toast } = useToast();
  const { t, currentLanguage } = useI18n();
  const [fullNotes, setFullNotes] = useState('');
  const [shareWithCommunity, setShareWithCommunity] = useState(false);
  const [privacyLevel, setPrivacyLevel] = useState<'public' | 'connections' | 'gym'>('public');
  const [selectedTechniques, setSelectedTechniques] = useState<string[]>([]);
  const [showTechniques, setShowTechniques] = useState(false);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [partnerSearchValue, setPartnerSearchValue] = useState<any>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [instructor, setInstructor] = useState<any>(null);
  const [instructorLoading, setInstructorLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredInstructors, setFilteredInstructors] = useState<any[]>([]);
  const [showManualOption, setShowManualOption] = useState(false);
  const [overallRating, setOverallRating] = useState<number | null>(null);

  // Feedback states - simplified for individual feedback only
  const [currentFeedbackPartner, setCurrentFeedbackPartner] = useState<any>(null);
  const [existingFeedback, setExistingFeedback] = useState<TrainingPartnerFeedback | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackReloadKey, setFeedbackReloadKey] = useState(0);

  // Get training notes data from props
  const trainingNotesData = propNotesData?.[training?.id || ''];
  const data = trainingNotesData;
  // Only show loading if we have a training but no data yet and we're expecting data
  const loading = false; // Simplified - we don't need loading state for this modal

  // Use partners from training_partners table (not from notes data anymore)
  const { partners, loading: partnersLoading, saving: partnersSaving, addPartner, removePartner, savePartners, loadPartners } = useTrainingPartnersFromData(
    training?.id || '',
    [] // Empty array since partners are now loaded from API
  );
  const { loading: feedbacksLoading, feedbacks, saveFeedback, getFeedback, hasFeedback, getFeedbackForPartner, getTrainingFeedbacks, clearFeedbacks } = usePartnerFeedback({ 
    trainingId: data?.notes?.id || training?.id 
  });
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  // Function to save notes using Edge Function
  const saveNotes = async (notesData: any) => {
    if (!training?.id) throw new Error('No training ID');
    
    const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
    const token = session?.access_token;

    if (!token) throw new Error('No access token');

    const baseUrl = import.meta.env.DEV
      ? 'http://127.0.0.1:54321'
      : import.meta.env.VITE_SUPABASE_URL;

    console.log('🔍 TrainingNotesModal - saveNotes called with data:', notesData);
    console.log('🔍 TrainingNotesModal - Making request to:', `${baseUrl}/functions/v1/save_training_notes`);

    const response = await fetch(`${baseUrl}/functions/v1/save_training_notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        training_id: training.id,
        ...notesData
      }),
    });

    console.log('🔍 TrainingNotesModal - Response status:', response.status);
    console.log('🔍 TrainingNotesModal - Response body:', await response.clone().text());

    if (!response.ok) {
      const errorData = await response.json();
      console.error('🔍 TrainingNotesModal - Error response:', errorData);
      throw new Error(errorData.error || 'Failed to save notes');
    }

    const result = await response.json();
    console.log('🔍 TrainingNotesModal - Success response:', result);
    return result;
  };

  useEffect(() => {
    if (data?.notes) {
      setFullNotes(data.notes.full_notes || '');
      setShareWithCommunity(data.notes.shared_with_community);
      setPrivacyLevel(data.notes.privacy_level || 'public');
      setOverallRating(data.notes.overall_rating || null);
    }
    if (data?.techniques) {
      // Convert technique names back to unique IDs for display
      const techniqueIds = data.techniques
        .map(technique => {
          // Find which category and index this technique belongs to
          for (const category of techniqueCategories) {
            const index = category.techniques.findIndex(t => t === technique.name);
            if (index !== -1) {
              return `${category.name}_${index + 1}`;
            }
          }
          return null;
        })
        .filter(Boolean);
      setSelectedTechniques(techniqueIds);
      
      // If there are techniques, keep the techniques section closed by default
      if (techniqueIds.length > 0) {
        setShowTechniques(false); // Keep closed by default
        setOpenCategories([]); // Close all categories by default
      }
    }
  }, [data]);

  // Load feedbacks when modal opens or feedbackReloadKey changes
  useEffect(() => {
    if (isOpen && training?.id) {
      const notesId = data?.notes?.id || training.id;
      getTrainingFeedbacks(notesId).catch(error => {
        console.error('Error loading feedbacks:', error);
      });
    }
  }, [isOpen, training?.id, data?.notes?.id, getTrainingFeedbacks]);


  const handleTechniqueSelect = (technique: string) => {
    setSelectedTechniques(prev => {
      if (prev.includes(technique)) {
        return prev.filter(t => t !== technique);
      } else {
        return [...prev, technique];
      }
    });
  };

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategories.includes(categoryName);
  };

  const handleShowTechniques = () => {
    setShowTechniques(!showTechniques);
    // Scroll to bottom after a short delay to allow content to render
    setTimeout(() => {
      const modalContent = document.querySelector('[data-radix-scroll-area-viewport]');
      if (modalContent) {
        modalContent.scrollTo({
          top: modalContent.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handlePartnerSelect = async (partner: any) => {
    // Handle null partner (when clearing the input)
    if (!partner) {
      return;
    }

    // Check if partner is already selected
    const isAlreadySelected = partners.some(p => 
      (p.partner_user_id && p.partner_user_id === partner.id) ||
      (p.manual_partner_name && p.manual_partner_name === partner)
    );

    if (isAlreadySelected) {
      toast({
        title: "Partner already added",
        description: "This partner is already in your list",
        variant: "destructive",
      });
      return;
    }

    // Add partner to list
    const newPartner: TrainingPartner = {
      id: typeof partner === 'string' ? `manual_${Date.now()}` : partner.id,
      partner_user_id: typeof partner === 'string' ? undefined : partner.id,
      manual_partner_name: typeof partner === 'string' ? partner : undefined,
      partner_name: typeof partner === 'string' ? partner : partner.full_name,
      partner_username: typeof partner === 'string' ? undefined : partner.username,
      partner_avatar_url: typeof partner === 'string' ? undefined : partner.profile_picture_url,
      partner_belt_rank: typeof partner === 'string' ? undefined : partner.belt_rank,
      is_manual: typeof partner === 'string'
    };

    try {
      await addPartner(newPartner);
      // Clear the search field after adding partner
      setPartnerSearchValue(null);
    } catch (error) {
      console.error('Error adding partner:', error);
    }
  };

  const handleRemovePartner = async (partnerId: string) => {
    console.log('🔍 TrainingNotesModal - handleRemovePartner called with partnerId:', partnerId);
    try {
      await removePartner(partnerId);
      console.log('�� TrainingNotesModal - removePartner completed successfully');
    } catch (error) {
      console.error('🔍 TrainingNotesModal - Error in handleRemovePartner:', error);
      // Error is already handled in the hook
    }
  };

  const handleSave = async () => {
    if (!training) return;

    console.log('🔍 TrainingNotesModal - Starting save process for training:', training.id);
    console.log('🔍 TrainingNotesModal - Current overallRating:', overallRating);
    setSaving(true);
    try {
      // Convert technique IDs to technique names (same as ClassNotesModal)
      const techniqueNames = selectedTechniques.map(techId => {
        const lastUnderscoreIndex = techId.lastIndexOf('_');
        const categoryName = techId.substring(0, lastUnderscoreIndex);
        const indexStr = techId.substring(lastUnderscoreIndex + 1);
        const index = parseInt(indexStr) - 1;
        
        const category = techniqueCategories.find(cat => cat.name === categoryName);
        if (category && category.techniques[index]) {
          return category.techniques[index];
        }
        return techId; // fallback to original ID
      });

      // Save notes with partners included

      await saveNotes({
        general_notes: '', // Not used in this implementation
        full_notes: fullNotes,
        shared_with_community: shareWithCommunity,
        privacy_level: privacyLevel,
        technique_ids: techniqueNames, // Use converted names
        overall_rating: overallRating
        // partners are managed separately via link_training_partners function
      });

      console.log('🔍 TrainingNotesModal - Notes saved successfully with rating:', overallRating);

      // Save partners to training (for backward compatibility)
      if (partners.length > 0) {
        console.log('🔍 TrainingNotesModal - Saving partners:', partners.length);
        await savePartners(partners);
        console.log('🔍 TrainingNotesModal - Partners saved successfully');
      }

      // Notify parent component that data was saved
      if (onDataSaved) {
        onDataSaved();
      }

      // Close the modal after saving
      console.log('🔍 TrainingNotesModal - Closing modal');
      onClose();
    } catch (error) {
      console.error('🔍 TrainingNotesModal - Error saving notes:', error);
      toast({
        title: "Error saving notes",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };


  const handleFeedbackModalClose = async (wasSaved = false) => {
    setShowFeedbackModal(false);
    
    // Reload feedbacks and update UI
    if (training) {
      try {
        
        // Reload feedbacks and update local state - use notes ID instead of training ID
        const notesId = data?.notes?.id || training.id;
        
        // Force reload feedbacks and wait for it to complete
        const updatedFeedbacks = await getTrainingFeedbacks(notesId);
        
        // Force refetch of notes data to update UI
        if (data?.notes?.id) {
          queryClient.invalidateQueries({ queryKey: ['training-notes', training.id] });
        }
        
        // Force multiple updates to ensure state is properly updated
        setTimeout(() => {
          setFeedbackReloadKey(prev => prev + 1);
        }, 50);
        
        setTimeout(() => {
          setFeedbackReloadKey(prev => prev + 1);
        }, 200);
        
        // Only show success message if we actually saved a feedback
        if (wasSaved) {
          toast({
            title: "Feedback saved!",
            description: "The training notes have been updated.",
          });
        }
      } catch (error) {
        console.error('Error updating feedbacks:', error);
        toast({
          title: "Error updating feedbacks",
          description: "Please refresh the page to see the latest changes.",
          variant: "destructive",
        });
      }
    }
  };

  const handleClose = () => {
    setFullNotes('');
    setShareWithCommunity(false);
    setPrivacyLevel('public');
    setSelectedTechniques([]);
    setShowTechniques(false);
    setOpenCategories([]);
    setPartnerSearchValue(null);
    setOverallRating(null);
    // Reset partners by loading empty array
    loadPartners([]);
    onClose();
  };

  if (!training) return null;

  const isCompleted = training.status === 'Completed';
  const hasNotes = !!data?.notes;

  // Build training info string without empty values
  const getLocalizedTrainingType = (type: string) => {
    switch (type) {
      case 'Group Class':
        return t('calendar.group_class');
      case 'Private Class':
        return t('calendar.private_class');
      default:
        return type;
    }
  };

  // Format date according to language
  const formatLocalizedDate = (date: Date) => {
    if (currentLanguage === 'pt') {
      // Portuguese format: "24 de agosto de 2025"
      const month = date.toLocaleDateString('pt-BR', { month: 'long' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${day} de ${month} de ${year}`;
    } else {
      // English format: "August 24, 2025"
      return format(date, 'MMMM d, yyyy');
    }
  };

  // Format time according to language
  const formatLocalizedTime = (date: Date) => {
    if (currentLanguage === 'pt') {
      // Portuguese format: "14:00 às 15:00"
      return format(date, 'HH:mm');
    } else {
      // English format: "2:00 PM"
      return format(date, 'h:mm a');
    }
  };

  const trainingInfoParts = [
    training.type ? getLocalizedTrainingType(training.type) : training.title,
    training.category,
    training.modality
  ].filter(Boolean); // Remove empty/undefined values

  const trainingInfoString = trainingInfoParts.join(' - ');

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-2xl bg-white rounded-2xl p-6" aria-describedby="training-notes-description">
        <DialogDescription id="training-notes-description" className="sr-only">
          {hasNotes ? t('calendar.edit_notes') : t('calendar.training_notes_description')}
        </DialogDescription>
        <SossietyDialogHeader 
          title={hasNotes ? t('calendar.edit_notes') : t('calendar.add_notes')}
          titleClassName="text-lg"
        >
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>
        
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
          {/* Training Info - Type, Category, Modality */}
          <div className="rounded-lg">
            <h3 className="text-black font-sans text-sm font-semibold mb-1">
              {trainingInfoString}
            </h3>
          </div>

          {/* Date */}
          <div className="rounded-lg !mt-0">
            <p className="text-gray-500 font-sans text-xs">
              {formatLocalizedDate(new Date(training.start_time))} - {formatLocalizedTime(new Date(training.start_time))} {currentLanguage === 'pt' ? 'às' : 'to'} {formatLocalizedTime(new Date(training.end_time))}
            </p>
          </div>

          {/* Overall Rating */}
          <div className="space-y-3 !mt-8">
            <label className="block text-sm font-medium text-black">
              {t('calendar.overall_rating')}
            </label>
            <RatingSelector
              value={overallRating}
              onChange={(rating) => {
                console.log('🔍 TrainingNotesModal - Rating changed to:', rating);
                setOverallRating(rating);
              }}
              disabled={saving}
            />
          </div>

          {/* Full Training Notes with Share with Community */}
          <div>
            <label className="block text-sm font-medium mb-2 mt-8 text-black">
              {t('calendar.full_training_notes')}
              <span className="text-gray-500 text-xs ml-1">
                ({fullNotes.length}/2000)
              </span>
            </label>
            
            <Textarea
              value={fullNotes}
              onChange={(e) => setFullNotes(e.target.value.slice(0, 2000))}
              placeholder={t('calendar.detailed_reflections')}
              className="text-black border-gray-300 bg-white resize-none text-sm"
              rows={8}
              maxLength={2000}
            />
            
            {/* Share with Community */}
            <div className="space-y-2 mt-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="share-community"
                  checked={shareWithCommunity}
                  onCheckedChange={(checked) => setShareWithCommunity(checked as boolean)}
                  disabled={saving}
                />
                <label htmlFor="share-community" className="text-sm font-medium text-black">
                  {t('calendar.share_with_community')}
                </label>
              </div>

              {shareWithCommunity && (
                <div className="!mt-5">
                  <label className="block text-xs text-black">{t('calendar.who_can_see_post')}</label>
                  <div className="w-full">
                    <Select value={privacyLevel} onValueChange={(value: 'public' | 'connections' | 'gym') => setPrivacyLevel(value)}>
                      <SelectTrigger className="w-full h-8 text-xs text-black relative [&_[data-radix-select-icon]]:hidden">
                        <SelectValue />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <ChevronDown className="w-4 h-4 text-black" />
                        </span>
                      </SelectTrigger>
                      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1">
                        <SelectItem value="public" className="text-white truncate">{t('social.whole_community')}</SelectItem>
                        <SelectItem value="connections" className="text-white truncate">{t('calendar.only_my_connections')}</SelectItem>
                        <SelectItem value="gym" className="text-white truncate">{t('calendar.only_my_gym')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Training Partners - For all training types */}
          <div className="space-y-3 !mt-8">
            <label className="block text-sm font-medium text-black">
              {t('calendar.training_partners')}
            </label>
            
            {/* Partner Search */}
            {!partnersLoading && (
              <PartnerAutocomplete
                key={`partner-search-${partners.length}`}
                value={partnerSearchValue}
                onChange={handlePartnerSelect}
                placeholder={t('calendar.type_name_or_username')}
                containerClassName="bg-white"
                disabled={saving}
              />
            )}

            {/* Loading Shimmer */}
            {partnersLoading && (
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                <div className="space-y-2">
                  {/* Shimmer Partner 1 */}
                  <div className="rounded-lg overflow-hidden relative">
                    <div className="p-3 pr-12">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-32 mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-20"></div>
                        </div>
                      </div>
                    </div>
                    <div className="px-3 pb-3">
                      <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                  
                  {/* Shimmer Partner 2 */}
                  <div className="rounded-lg overflow-hidden relative">
                    <div className="p-3 pr-12">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-28 mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-16"></div>
                        </div>
                      </div>
                    </div>
                    <div className="px-3 pb-3">
                      <div className="flex gap-2">
                        <div className="h-6 bg-gray-200 rounded animate-pulse flex-1"></div>
                        <div className="h-6 bg-gray-200 rounded animate-pulse flex-1"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Selected Partners List */}
            {!partnersLoading && partners.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-gray-600">
                  {t('calendar.selected_partners', { 
                    count: partners.length, 
                    plural: partners.length !== 1 ? 's' : '' 
                  })}
                </div>
                <div className="space-y-2">
                  {partners.map((partner) => {
                    const hasExistingFeedback = hasFeedback(partner.partner_user_id || '');
                    const existingFeedbackData = getFeedbackForPartner ? getFeedbackForPartner(partner.partner_user_id || '') : null;
                    
                    return (
                      <div key={partner.id} className="rounded-lg overflow-hidden relative">
                        {/* Remove Button - Top Right Corner */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePartner(partner.id)}
                          className="absolute top-2 right-2 h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        
                        {/* Partner Info Row */}
                        <div className="p-3 pr-12">
                          <PartnerDisplay
                            partner={partner}
                            showRemoveButton={false}
                          />
                        </div>
                        
                        {/* Feedback Buttons */}
                        <div className="px-3 pb-3">
                          {hasExistingFeedback ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setCurrentFeedbackPartner(partner);
                                setExistingFeedback(existingFeedbackData);
                                setShowFeedbackModal(true);
                              }}
                              className="w-full border-black text-black hover:bg-gray-50 rounded-full text-xs"
                            >
                              {t('calendar.view_feedback')}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setCurrentFeedbackPartner(partner);
                                setExistingFeedback(null);
                                setShowFeedbackModal(true);
                              }}
                              className="w-full border-black text-black hover:bg-gray-50 rounded-full text-xs"
                            >
                              {t('calendar.add_feedback')}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Link Techniques */}
          <div className="rounded-lg !mt-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <label className="text-sm font-medium text-black">
                  {t('calendar.link_techniques')}
                </label>
              </div>
              <button
                type="button"
                onClick={handleShowTechniques}
                className="text-xs text-[#D72E16] hover:text-[#B8251A] font-medium underline"
              >
                {showTechniques ? t('calendar.hide_techniques') : t('calendar.show_techniques')}
              </button>
            </div>
            
            {!showTechniques && selectedTechniques.length === 0 && (
              <div className="text-center mt-3 mb-3">
                <p className="text-xs font-medium text-gray-600">
                  {t('calendar.register_techniques_used')}
                </p>
                <p className="text-xs text-gray-600">
                  {t('calendar.techniques_filter_description')}
                </p>
              </div>
            )}

            {selectedTechniques.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-600 mb-2">
                  Selected: {selectedTechniques.length} technique{selectedTechniques.length !== 1 ? 's' : ''}
                </div>
                <div className="flex flex-wrap gap-1">
                  {selectedTechniques.map((techId) => {
                    // Find the technique name by unique ID (category_index format)
                    let techniqueName = techId;
                    const lastUnderscoreIndex = techId.lastIndexOf('_');
                    const categoryName = techId.substring(0, lastUnderscoreIndex);
                    const indexStr = techId.substring(lastUnderscoreIndex + 1);
                    const index = parseInt(indexStr) - 1;
                    
                    const category = techniqueCategories.find(cat => cat.name === categoryName);
                    if (category && category.techniques[index]) {
                      techniqueName = category.techniques[index];
                    }
                    
                    // Use the same translation key pattern as the collapsible buttons
                    return (
                      <span
                        key={techId}
                        className="px-2 py-1 bg-[#D72E16] text-white rounded-full text-xs font-medium"
                      >
                        {techniqueName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {showTechniques && (
              <div className="space-y-3 mt-4">
                {techniqueCategories.map((category) => (
                  <Collapsible 
                    key={category.name} 
                    open={isCategoryOpen(category.name)} 
                    onOpenChange={() => toggleCategory(category.name)}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <span>{t(`calendar.techniques.${category.name}`)}</span>
                      {isCategoryOpen(category.name) ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="flex flex-wrap gap-1">
                        {category.techniques.map((technique, index) => {
                          // Create unique technique ID by combining category and index
                          const techniqueId = `${category.name}_${index + 1}`;
                          const isSelected = selectedTechniques.includes(techniqueId);
                          return (
                            <button
                              key={technique}
                              type="button"
                              onClick={() => handleTechniqueSelect(techniqueId)}
                              className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                                isSelected
                                  ? 'bg-[#D72E16] text-white border-[#D72E16]'
                                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                              }`}
                            >
                              {technique}
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <Button
            className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-2.5 h-10 rounded-full transition-colors duration-200 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving || loading || partnersSaving}
          >
            {saving || partnersSaving ? t('calendar.saving') : (hasNotes ? t('calendar.update_notes') : t('calendar.save_notes'))}
          </Button>
        </div>
      </DialogContent>

      {/* Partner Feedback Modal */}
      {currentFeedbackPartner && (
        <PartnerFeedbackModal
          isOpen={showFeedbackModal}
          onClose={(wasSaved = false) => handleFeedbackModalClose(wasSaved)}
          partner={currentFeedbackPartner}
          trainingId={training?.id || null}
          trainingNotesId={data?.notes?.id || null}
          existingFeedback={existingFeedback}
        />
      )}
    </Dialog>
  );
}; 